/**
 * document-scanner / index.js — controller.
 *
 * TWO LEVELS, NOT FOUR STEPS
 * ──────────────────────────
 *   doc   — every page at once, plus export. The place you come back to.
 *   page  — one page: its frame and its look, switched by a toggle, never by a
 *           page change. The place you fix things.
 *   cam   — transient.
 *
 * The build this replaces was four full-width stages stacked vertically with a
 * 72vh canvas above the controls, so on a 1280×720 laptop the controls sat
 * below the fold and on a 390×844 phone the primary button landed behind the
 * cookie bar. It also asked every user to confirm a crop that the detector had
 * already got right.
 *
 * DETECTION IS INSTANT, THEN BETTER
 * ─────────────────────────────────
 * The classical detector is 41–163 ms even on a 2400×3200 source (measured
 * 2026-08-28 across ten synthetic desk photos; 9/10 gave a frame within 5% of
 * the true page area). The DocAligner model is 4.7 MB and its fetch has no
 * deadline. So the classical frame is drawn immediately and the model refines
 * it in the background under a timeout — nobody waits on a download to see a
 * frame, and nobody is stuck behind a slow one. If the user has already touched
 * a corner, the refinement is dropped: their hand outranks the model.
 *
 * WHEN DETECTION FAILS it opens the frame editor and says so, instead of
 * quietly warping the whole photo and handing back a scan of the desk.
 */

'use strict';

import * as CVD from './cv-detect.js';
import * as WARP from './cv-warp.js';
import * as ENH from './cv-enhance.js';
import * as MLD from './ml-detect.js';
import { buildPdf, pairUp } from './pdf-export.js';
import * as EDGE from './edge-refine.js';
import * as PQ from './page-quality.js';

/* ─── environment ─────────────────────────────────────────────────────────── */

const CFG = window.DS_CFG || {};
const T = (k) => (CFG.t && CFG.t[k] != null ? CFG.t[k] : k);
const SLUG = CFG.slug || 'document-scanner';
const $ = (id) => document.getElementById(id);
const toast = (m, kind) => (window.__toast ? window.__toast(m, kind) : console.log('[toast]', kind, m));
const announce = (m) => { try { window.__announce && window.__announce(m); } catch (_) {} };

const stage = (n, d) => { try { window.__stage && window.__stage(n, d); } catch (_) {} };
const funnel = (n, d) => { try { window.__aeFunnel && window.__aeFunnel(n, d); } catch (_) {} };
const diag = (d) => { try { window.__aeDiagnostic && window.__aeDiagnostic(d); } catch (_) {} };
const complete = (d) => { try { window.__aeToolComplete && window.__aeToolComplete(d); } catch (_) {} };

const ST = window.__toolSettings;
const pref = (k, d) => { try { return ST ? ST.load(SLUG, k, d) : d; } catch (_) { return d; } };
const setPref = (k, v) => { try { ST && ST.save(SLUG, k, v); } catch (_) {} };

/** Source photos are capped here; the warp output there. Both are quality
 *  ceilings, not guesses: 2400 px on the long edge is ~300 dpi across an A4
 *  page, which is more than any OCR engine uses. */
/**
 * How many pixels a captured page is allowed to keep, and how many the exported
 * sheet is allowed to have.
 *
 * These two constants were 2400 and 1900, and together they put a ceiling on
 * the tool that no setting could lift: an A4 page warped to 1900 px on its long
 * edge is 1343 px across, which is 162 dpi. The forms this tool exists to feed
 * commonly ask for 150-300 dpi, and the dpi requirement is the stricter half of
 * their rules — so the scanner could not satisfy the requirement it is most
 * often handed, no matter how good the photo was. Measured on the corpus: an
 * exported A4 came out at 112 dpi.
 *
 * Now the export resolution is a choice (150/200/300) and the capture keeps
 * enough pixels to honour it, bounded by what the device can actually hold. It
 * is still never UPSCALED past what the photo contains: claiming 300 dpi by
 * interpolation would be a lie told in pixels, and the tool reports the density
 * it really achieved instead.
 */
const MAX_SRC = (function () {
    var mem = (typeof navigator !== 'undefined' && navigator.deviceMemory) || 0;
    return mem >= 8 ? 4000 : mem >= 4 ? 3200 : 2400;
})();
const WARP_FULL = 1900;          // preview/no-sheet fallback
const DPI_CHOICES = { screen: 120, normal: 200, print: 300 };
/** Total exported pixels the page may hold at once, by device memory. Pages are
 *  built into an array before the PDF is assembled, so this is a real ceiling,
 *  not a guess: 300 dpi A4 is 8.7 Mpx and 35 MB of canvas per page. */
const PIXEL_BUDGET = (function () {
    var mem = (typeof navigator !== 'undefined' && navigator.deviceMemory) || 0;
    return (mem >= 8 ? 120 : mem >= 4 ? 60 : 30) * 1e6;
})();
const WARP_PREV = 900;      // the live-preview warp — filters must feel instant
const ML_DEADLINE = 6000;   // after this the model refinement is not worth waiting for

/* ─── state ───────────────────────────────────────────────────────────────── */

const S = {
    view: 'doc',
    pages: [],          // page records, in document order
    cur: -1,            // index being edited in `page` view
    queue: [],          // sources still waiting to be reviewed (multi-file pick)
    docKey: null,       // cap identity — one document is one run, however many pages
    tab: 'look',        // page view: 'look' | 'frame'
    open: false,
    cam: { stream: null, track: null, facing: 'environment', auto: pref('autocapture', true), raf: 0, last: 0, stable: 0, quad: null, busy: false, torch: false },
    exp: { fmt: 'pdf', size: pref('pagesize', 'a4'), quality: pref('quality', 'normal'), ocr: false, twoUp: false, name: '', fitMb: +pref('fitmb', 0) || 0, dpi: pref('dpi', 'normal') },
    drag: -1,
    corner: -1,
    dispScale: 1,
};

let ocrWorker = null;
let pdfjsP = null;
/** A page cap, not a size cap: past a few dozen pages this stops being "clean up
 *  my scan" and becomes a batch job the browser should not silently attempt. */
const PDF_MAX_PAGES = 30;
let nextId = 1;

function newPage(srcCanvas) {
    return {
        id: nextId++,
        src: srcCanvas,
        quad: fullQuad(srcCanvas.width, srcCanvas.height),
        from: 'manual',
        touched: false,
        filter: pref('mode', 'color'),
        rot: 0,
        bright: 0,
        contrast: 1,
        deskew: pref('deskew', true),
        prevWarp: null,     // low-res warp for live preview
        out: null,          // enhanced preview canvas
        thumb: null,        // cached data URL — never regenerate on every render
    };
}

/* ─── small helpers ───────────────────────────────────────────────────────── */

const show = (el) => { if (el) el.hidden = false; };
const hide = (el) => { if (el) el.hidden = true; };
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

function rgbaOf(c) {
    return c.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, c.width, c.height).data;
}
function canvasFrom(data, w, h) {
    const c = document.createElement('canvas'); c.width = w; c.height = h;
    c.getContext('2d').putImageData(new ImageData(new Uint8ClampedArray(data), w, h), 0, 0);
    return c;
}
function fullQuad(w, h) {
    const i = 0.02;
    return [{ x: w * i, y: h * i }, { x: w * (1 - i), y: h * i }, { x: w * (1 - i), y: h * (1 - i) }, { x: w * i, y: h * (1 - i) }];
}
function insetQuad(q, frac) {
    const cx = (q[0].x + q[1].x + q[2].x + q[3].x) / 4, cy = (q[0].y + q[1].y + q[2].y + q[3].y) / 4;
    return q.map((p) => ({ x: p.x + (cx - p.x) * frac, y: p.y + (cy - p.y) * frac }));
}
function rotateCanvas(c, deg) {
    if (!deg) return c;
    const swap = deg % 180 !== 0;
    const out = document.createElement('canvas');
    out.width = swap ? c.height : c.width;
    out.height = swap ? c.width : c.height;
    const g = out.getContext('2d');
    g.translate(out.width / 2, out.height / 2);
    g.rotate((deg * Math.PI) / 180);
    g.drawImage(c, -c.width / 2, -c.height / 2);
    return out;
}
function quadArea(q) {
    let s = 0;
    for (let i = 0; i < 4; i++) { const a = q[i], b = q[(i + 1) % 4]; s += a.x * b.y - b.x * a.y; }
    return Math.abs(s / 2);
}

/** Is the rail a static column right now? Asks the container query for its
 *  answer instead of restating the breakpoint in JS, where it would drift. */
function railIsColumn() {
    const r = $('dsRail');
    return !!r && getComputedStyle(r).position === 'static';
}

/* ─── busy / status surfaces ──────────────────────────────────────────────── */

function hud(text, kind) {
    const el = $('dsHud');
    if (!text) { hide(el); return; }
    $('dsHudTxt').textContent = text;
    el.className = 'ds-hud' + (kind ? ' is-' + kind : '');
    show(el);
}
function veil(text) {
    if (!text) { hide($('dsVeil')); return Promise.resolve(); }
    $('dsVeilTxt').textContent = text;
    show($('dsVeil'));
    // Two frames so the veil is actually painted before a synchronous pass
    // starts — otherwise it appears only after the work it was meant to cover.
    return new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
}
function progress(frac, text) {
    show($('dsProg'));
    $('dsProgBar').style.width = Math.max(0, Math.min(1, frac)) * 100 + '%';
    if (text != null) $('dsProgTxt').textContent = text;
}
const progressDone = () => hide($('dsProg'));

/** A quality choice with no feedback is a guess. Reporting the real size of the
 *  file just handed over costs nothing and gives the next choice a reference
 *  point — an estimate before the fact would only be a guess of our own. */
function fmtSize(bytes) {
    if (!(bytes > 0)) return '';
    return bytes >= 1048576
        ? (bytes / 1048576).toFixed(bytes >= 10485760 ? 0 : 1) + ' ' + T('mb')
        : Math.round(bytes / 1024) + ' ' + T('kb');
}
const withSize = (msg, blob) => (blob && blob.size ? msg + ' · ' + fmtSize(blob.size) : msg);

/* ─── detection ───────────────────────────────────────────────────────────── */

/**
 * Both detectors propose a quad from CONTRAST, and inside a document the
 * strongest contrast is text — so both of them cut a page off at its last
 * printed line whenever the lower part is empty and the desk is pale. Measured
 * on a 24-photo corpus with exact ground truth: 8 of 24 crops wrong, every one
 * of them that same way, which is the field's 35 % `manual_adjust` rate.
 *
 * So neither proposal is trusted as final — each one is snapped onto the real
 * paper boundary first (edge-refine.js). Same corpus after: 1 of 24 wrong,
 * median corner error 0.79 % → 0.06 % of the frame, ~15 ms.
 *
 * The confidence that comes back is kept on the page: it decides whether the
 * frame step is worth stopping at, and it is the honest answer to the one photo
 * that is still hard (white paper on a white desk in shadow) — there the tool
 * says it is unsure instead of shipping a confident bad crop.
 */
function snapToPaper(rgba, w, h, quad, page) {
    try {
        const r = EDGE.refine(rgba, w, h, quad);
        page.conf = r.confidence;
        if (r.changed) stage('edge_snapped', { conf: r.confidence });
        return r.quad;
    } catch (e) {
        diag({ issue: 'edge_refine_failed', msg: String((e && e.message) || e).slice(0, 80) });
        page.conf = null;
        return quad;
    }
}

/**
 * Classical frame now, model refinement later. `onFrame` is called once
 * immediately and possibly a second time when the model lands.
 */
function detect(page, onFrame) {
    const w = page.src.width, h = page.src.height;
    const rgba = rgbaOf(page.src);

    let quick = null;
    try { quick = CVD.detectQuad(rgba, w, h); } catch (_) { quick = null; }
    if (quick) { quick = snapToPaper(rgba, w, h, quick, page); page.quad = quick; page.from = 'cv'; }
    else { page.quad = fullQuad(w, h); page.from = 'manual'; page.conf = 0; }
    funnel('detect', { src: page.from, conf: page.conf });
    onFrame(page.from);

    const t0 = performance.now();
    const race = Promise.race([
        MLD.detectCorners(rgba, w, h, { modelUrl: CFG.onnx }),
        new Promise((_, rej) => setTimeout(() => rej(new Error('ml-deadline')), ML_DEADLINE)),
    ]);
    race.then((q) => {
        if (!q) return;
        // Their hand outranks the model: a corner already dragged is not undone.
        if (page.touched || S.pages[S.cur] !== page) return;
        // And a refinement that disagrees wildly with a confident classical
        // frame is more likely to be the model finding the desk than the page.
        if (quick && quadArea(q) < quadArea(quick) * 0.45) return;
        page.quad = snapToPaper(rgba, w, h, q, page); page.from = 'ml';
        page.prevWarp = null; page.out = null;
        stage('model_refined', { ms: Math.round(performance.now() - t0) });
        onFrame('ml');
    }).catch((e) => {
        const m = String((e && e.message) || e);
        if (m !== 'ml-deadline') diag({ issue: 'model_unavailable', msg: m.slice(0, 80) });
        else diag({ issue: 'model_slow', ms: ML_DEADLINE });
    });
}

/* ─── rendering a page ────────────────────────────────────────────────────── */

function stageBox() {
    const st = $('dsStage');
    return { w: Math.max(80, st.clientWidth - 24), h: Math.max(80, st.clientHeight - 24) };
}

/** The frame editor: the source photo with a dimmed surround and four handles. */
function drawFrame() {
    const p = S.pages[S.cur]; if (!p) return;
    const c = $('dsCanvas'), g = c.getContext('2d');
    const box = stageBox();
    const sc = Math.min(box.w / p.src.width, box.h / p.src.height, 1);
    c.width = Math.round(p.src.width * sc);
    c.height = Math.round(p.src.height * sc);
    S.dispScale = sc;
    g.drawImage(p.src, 0, 0, c.width, c.height);

    const q = p.quad.map((pt) => ({ x: pt.x * sc, y: pt.y * sc }));
    g.save();
    g.fillStyle = 'rgba(0,0,0,.5)';
    g.beginPath();
    g.rect(0, 0, c.width, c.height);
    g.moveTo(q[0].x, q[0].y); g.lineTo(q[3].x, q[3].y); g.lineTo(q[2].x, q[2].y); g.lineTo(q[1].x, q[1].y);
    g.closePath(); g.fill('evenodd');
    g.restore();

    g.strokeStyle = '#38bdf8'; g.lineWidth = 2.5; g.lineJoin = 'round';
    g.beginPath(); g.moveTo(q[0].x, q[0].y);
    for (let i = 1; i < 4; i++) g.lineTo(q[i].x, q[i].y);
    g.closePath(); g.stroke();

    for (let i = 0; i < 4; i++) {
        g.beginPath(); g.arc(q[i].x, q[i].y, 12, 0, Math.PI * 2);
        g.fillStyle = 'rgba(255,255,255,.96)'; g.fill();
        g.lineWidth = 1; g.strokeStyle = 'rgba(0,0,0,.12)'; g.stroke();
        g.beginPath(); g.arc(q[i].x, q[i].y, 6.5, 0, Math.PI * 2);
        g.fillStyle = '#0ea5e9'; g.fill();
    }
}

/** Warp + enhance at PREVIEW resolution. Filters have to feel like a switch,
 *  not like a job: the full-resolution pass happens once, at export. */
function renderLook() {
    const p = S.pages[S.cur]; if (!p) return Promise.resolve();
    return veil(T('processing')).then(() => {
        try {
            if (!p.prevWarp) {
                const rgba = rgbaOf(p.src);
                let wp = WARP.warp(rgba, p.src.width, p.src.height, insetQuad(p.quad, 0.004), { maxEdge: WARP_PREV });
                if (p.deskew) {
                    try { const a = ENH.deskewAngle(wp.data, wp.w, wp.h); if (a) wp = rotateWarped(wp, -a); } catch (_) {}
                }
                p.prevWarp = wp;
            }
            const cp = new Uint8ClampedArray(p.prevWarp.data);
            // Judged BEFORE the enhancement, on the raw warped page: flattening
            // and stretching make a dark photo look bright and a soft one look
            // contrasty, which is exactly the make-up that hides the problem the
            // user needs to know about while re-shooting still costs seconds.
            if (p.quality === undefined) {
                try { p.quality = PQ.assess(p.prevWarp.data, p.prevWarp.w, p.prevWarp.h); }
                catch (_) { p.quality = null; }
                if (p.quality && p.quality.verdict !== 'ok') {
                    funnel('page_quality', { v: p.quality.verdict, sharp: p.quality.sharp, expo: p.quality.expo });
                }
            }
            showQualityNote(p);
            ENH.enhance(cp, p.prevWarp.w, p.prevWarp.h, p.filter, { brightness: p.bright, contrast: p.contrast });
            p.out = rotateCanvas(canvasFrom(cp, p.prevWarp.w, p.prevWarp.h), p.rot);
            p.thumb = null;
            paintOut();
        } catch (e) {
            toast(T('err_export'), 'error');
            diag({ issue: 'render_failed', msg: String((e && e.message) || e).slice(0, 90) });
        }
        hide($('dsVeil'));
    });
}

/**
 * Says, once, that this page will probably not read — and offers the only
 * repair that helps, which is another photo.
 *
 * The worst thing a scanner can do is not to fail. It is to hand over a file
 * that looks fine in a thumbnail, gets sent to a bank, and is refused two days
 * later because the text was smeared. That refusal is expensive; re-shooting
 * costs five seconds, and the only moment the tool can offer that trade is now.
 *
 * Never blocks and never nags: one line, dismissible, and the export button
 * stays exactly as live as it was. The threshold's margin is narrow, so the
 * text is an observation with an offer, not a verdict.
 */
function showQualityNote(p) {
    const box = $('dsQNote'); if (!box) return;
    const v = p.quality && p.quality.verdict;
    if (!v || v === 'ok' || p.qNoteDismissed) { hide(box); return; }
    $('dsQNoteTxt').textContent = v === 'blur' ? T('q_blur') : T('q_dark');
    show(box);
}

function rotateWarped(wp, deg) {
    const src = canvasFrom(wp.data, wp.w, wp.h);
    const c = document.createElement('canvas'); c.width = wp.w; c.height = wp.h;
    const g = c.getContext('2d');
    g.fillStyle = '#fff'; g.fillRect(0, 0, wp.w, wp.h);
    g.translate(wp.w / 2, wp.h / 2); g.rotate((deg * Math.PI) / 180);
    g.drawImage(src, -wp.w / 2, -wp.h / 2);
    return { data: g.getImageData(0, 0, wp.w, wp.h).data, w: wp.w, h: wp.h };
}

function paintOut() {
    const p = S.pages[S.cur]; if (!p || !p.out) return;
    const c = $('dsCanvas'), g = c.getContext('2d');
    const box = stageBox();
    const sc = Math.min(box.w / p.out.width, box.h / p.out.height, 1);
    c.width = Math.round(p.out.width * sc);
    c.height = Math.round(p.out.height * sc);
    g.drawImage(p.out, 0, 0, c.width, c.height);
}

/** Full-resolution pass, done once per page at export time. */
function renderFull(p, maxEdge) {
    const rgba = rgbaOf(p.src);
    let wp = WARP.warp(rgba, p.src.width, p.src.height, insetQuad(p.quad, 0.004), { maxEdge: maxEdge || WARP_FULL });
    if (p.deskew) {
        try { const a = ENH.deskewAngle(wp.data, wp.w, wp.h); if (a) wp = rotateWarped(wp, -a); } catch (_) {}
    }
    const cp = new Uint8ClampedArray(wp.data);
    ENH.enhance(cp, wp.w, wp.h, p.filter, { brightness: p.bright, contrast: p.contrast });
    const c = rotateCanvas(canvasFrom(cp, wp.w, wp.h), p.rot);
    return { canvas: c, w: c.width, h: c.height };
}

/* ─── loupe ───────────────────────────────────────────────────────────────── */

function showLoupe(idx) {
    const p = S.pages[S.cur]; if (!p) return;
    const c = $('dsCanvas'), lp = $('dsLoupe'), lc = $('dsLoupeCanvas');
    const q = p.quad[idx], L = lc.width, zoom = 3, g = lc.getContext('2d');
    g.fillStyle = '#fff'; g.fillRect(0, 0, L, L);
    const s = L / zoom;
    try { g.drawImage(p.src, q.x - s / 2, q.y - s / 2, s, s, 0, 0, L, L); } catch (_) {}
    g.strokeStyle = 'rgba(56,189,248,.95)'; g.lineWidth = 1.5;
    g.beginPath();
    g.moveTo(L / 2 - 14, L / 2); g.lineTo(L / 2 + 14, L / 2);
    g.moveTo(L / 2, L / 2 - 14); g.lineTo(L / 2, L / 2 + 14);
    g.stroke();
    g.strokeStyle = 'rgba(56,189,248,.55)';
    g.beginPath(); g.arc(L / 2, L / 2, 16, 0, Math.PI * 2); g.stroke();

    const st = $('dsStage'), sr = st.getBoundingClientRect(), cr = c.getBoundingClientRect();
    const px = cr.left - sr.left + q.x * S.dispScale;
    const py = cr.top - sr.top + q.y * S.dispScale;
    let lx = px - L / 2, ly = py - L - 26;
    if (ly < 4) ly = py + 26;
    lx = Math.max(4, Math.min(sr.width - L - 4, lx));
    ly = Math.max(4, Math.min(sr.height - L - 4, ly));
    lp.style.left = lx + 'px'; lp.style.top = ly + 'px'; lp.style.display = 'block';
}
const hideLoupe = () => { const l = $('dsLoupe'); if (l) l.style.display = 'none'; };

/* ─── the work surface ────────────────────────────────────────────────────── */

/**
 * The layout parks fixed bars at both edges of the viewport, and some of them
 * are ABOVE this workspace on purpose: the cookie banner at z-index 9998
 * (consent must stay reachable) and the Premium bar at 1100. Measured
 * 2026-08-28, both landed straight on top of the workspace chrome — the promo
 * bar covered the back button and the title outright on a phone.
 *
 * So rather than naming them, ask the page: any visible fixed element stacked
 * above us and touching an edge gets its height reserved at that edge. Nothing
 * is hidden, nothing is unreachable, and a bar added to the layout tomorrow is
 * handled without touching this file.
 */
const WORK_Z = 160;
let barObs = null;
function reserveForBars() {
    const work = $('dsWork');
    let top = 0, bottom = 0;
    const vh = window.innerHeight;
    try {
        document.querySelectorAll('body *').forEach((el) => {
            if (el === work || work.contains(el)) return;
            const cs = getComputedStyle(el);
            if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden') return;
            if (!(parseInt(cs.zIndex, 10) > WORK_Z)) return;
            const r = el.getBoundingClientRect();
            if (r.height < 4 || r.height > vh * 0.4) return;      // a full-height overlay is not an edge bar
            if (r.top <= 2) top = Math.max(top, r.bottom);
            else if (r.bottom >= vh - 2) bottom = Math.max(bottom, vh - r.top);
        });
    } catch (_) {}
    work.style.setProperty('--ds-top', Math.round(top) + 'px');
    work.style.setProperty('--ds-bottom', Math.round(bottom) + 'px');
}
function watchBanner(on) {
    if (!on) { if (barObs) { barObs.disconnect(); barObs = null; } return; }
    reserveForBars();
    if (barObs || typeof MutationObserver !== 'function') return;
    barObs = new MutationObserver(reserveForBars);
    barObs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden', 'style'] });
}

let scrollY = 0;
function openWork() {
    if (S.open) return;
    S.open = true;
    scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    show($('dsWork'));
    $('dsWork').classList.add('is-open');
    watchBanner(true);
    stage('workspace_open');
}
function closeWork() {
    if (!S.open) return;
    S.open = false;
    stopCamera();
    watchBanner(false);
    document.body.style.overflow = '';
    $('dsWork').classList.remove('is-open');
    hide($('dsWork'));
    // No scrollIntoView, no scrollTo-to-element: restore exactly where they were.
    window.scrollTo(0, scrollY);
}

function setSheet(on) {
    $('dsWork').classList.toggle('is-sheet', !!on && !railIsColumn());
}

/* ─── view dispatch ───────────────────────────────────────────────────────── */

/**
 * Swapping views is instant, deliberately.
 *
 * `document.startViewTransition` was tried here and REMOVED after looking at it:
 * the two views do not share a layout — the rail holds filters in one and export
 * options in the other — so the root cross-fade renders both panes on top of
 * each other for the length of the animation. Caught on a 844×390 landscape
 * phone, where "ИМЯ ФАЙЛА / ФОРМАТ" and "Вид / Рамка" ghosted through one
 * another and the title read "Страница 1 из 11". Naming only the stage would
 * animate the one region that barely changes and leave the artefact in the rail.
 * A newer API is not a better one when it makes the screen look broken.
 */
function go(view) {
    S.view = view;
    render();
}

function render() {
    const v = S.view;
    const p = S.pages[S.cur];

    // stage children
    $('dsVideo').hidden = v !== 'cam';
    $('dsOverlay').hidden = v !== 'cam';
    $('dsCanvas').hidden = v !== 'page';
    $('dsGrid').hidden = v !== 'doc';
    if (v !== 'page') hideLoupe();
    // The status pill belongs to the screen that raised it. Left standing, the
    // page view's "drag the corners" advice floats over the document grid,
    // where there are no corners to drag — and it covers a page thumbnail.
    if (v === 'doc') hud(null);

    // rail panes
    $('dsPaneCam').hidden = v !== 'cam';
    $('dsPanePage').hidden = v !== 'page';
    $('dsPaneDoc').hidden = v !== 'doc';

    // top bar
    $('dsRotate').hidden = v !== 'page';
    $('dsTorch').hidden = !(v === 'cam' && S.cam.track && capsOf().torch);
    $('dsSwitch').hidden = v !== 'cam';
    $('dsBack').hidden = false;

    const n = S.pages.length;
    if (v === 'cam') $('dsTitle').innerHTML = '<b>' + esc(T('cam_open')) + '</b>' + (n ? ' <i>· ' + n + '</i>' : '');
    else if (v === 'page') $('dsTitle').innerHTML = '<b>' + esc(T('page_n').replace('%d', S.cur + 1)) + '</b> <i>' + T('page_of').replace('%d', n) + '</i>';
    else $('dsTitle').innerHTML = '<b>' + esc(T('doc_title')) + '</b> <i>· ' + T('pages_n').replace('%d', n) + '</i>';

    // filmstrip: only where it means something
    const strip = $('dsStrip');
    if (v === 'page' && n > 1) { renderStrip(); show(strip); } else hide(strip);

    // dock
    const cta = $('dsCta');
    $('dsShutter').hidden = v !== 'cam';
    $('dsCmdGap').hidden = v !== 'cam';
    renderTabs();

    if (v === 'cam') {
        cta.hidden = true;
    } else if (v === 'page') {
        cta.hidden = false;
        cta.textContent = S.queue.length ? T('btn_add') + ' (' + (S.queue.length + 1) + ')' : T('done');
        cta.disabled = false;
    } else {
        cta.hidden = false;
        cta.innerHTML = dlIcon() + esc(T('download'));
        cta.disabled = n === 0;
    }
    // The cap dims the button it is told to watch, and this one is shared: in
    // the page view it means "Done", which is never blocked by a spent cap. A
    // working control must not look spent.
    if (v === 'page') cta.classList.remove('tl-btn-blocked');
    else if (v === 'doc') {
        const TL = window.__toolLimit;
        let spent = false;
        try { spent = !!TL && (TL.blocked(SLUG) || !TL.allowed(SLUG)); } catch (_) {}
        cta.classList.toggle('tl-btn-blocked', spent);
    }

    if (v === 'page' && p) syncPageControls();
    if (v === 'doc') { renderGrid(); syncDocControls(); }
    if (v === 'cam') setSheet(false);
}

function esc(s) { return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])); }
function dlIcon() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/></svg>';
}

/** The tab strip only exists while the rail is a sheet; on a wide container the
 *  container query hides it and the rail is simply there. */
function renderTabs() {
    const wrap = $('dsTabs');
    if (railIsColumn()) { wrap.innerHTML = ''; return; }
    const tabs = S.view === 'cam' ? [['cam', T('cam_capture')]]
        : S.view === 'page' ? [['look', T('tab_look')], ['frame', T('tab_frame')]]
            : [['export', T('tab_export')]];
    wrap.innerHTML = '';
    tabs.forEach(([id, label]) => {
        const b = document.createElement('button');
        b.type = 'button'; b.textContent = label; b.dataset.tab = id;
        b.className = ($('dsWork').classList.contains('is-sheet') && (S.view !== 'page' || S.tab === id)) ? 'is-on' : '';
        wrap.appendChild(b);
    });
}

/* ─── page view controls ──────────────────────────────────────────────────── */

function setTab(tab) {
    S.tab = tab;
    $('dsFrameOpts').hidden = tab !== 'frame';
    $('dsLookOpts').hidden = tab !== 'look';
    Array.from($('dsPageTabs').children).forEach((b) => b.classList.toggle('is-on', b.dataset.pt === tab));
    const c = $('dsCanvas');
    c.style.cursor = tab === 'frame' ? 'crosshair' : 'default';
    if (tab === 'frame') { drawFrame(); hud(S.pages[S.cur] && S.pages[S.cur].from === 'manual' ? T('no_doc') : T('adjust_hint'), S.pages[S.cur] && S.pages[S.cur].from === 'manual' ? 'warn' : null); }
    else { hud(null); renderLook(); }
    renderTabs();
}

function syncPageControls() {
    const p = S.pages[S.cur]; if (!p) return;
    $('dsBright').value = p.bright;
    $('dsBrightOut').textContent = p.bright;
    $('dsContrast').value = Math.round(p.contrast * 100);
    $('dsContrastOut').textContent = Math.round(p.contrast * 100);
    $('dsDeskew').checked = !!p.deskew;
    Array.from($('dsFilters').children).forEach((b) => b.classList.toggle('is-on', b.dataset.f === p.filter));
}

function renderStrip() {
    const strip = $('dsStrip');
    strip.innerHTML = '';
    S.pages.forEach((p, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'ds-thumb' + (i === S.cur ? ' is-on' : '');
        b.title = T('page_n').replace('%d', i + 1);
        b.setAttribute('aria-label', b.title);
        const img = document.createElement('img');
        img.alt = ''; img.src = thumbOf(p);
        const tag = document.createElement('i'); tag.textContent = i + 1;
        b.append(img, tag);
        b.addEventListener('click', () => editPage(i));
        strip.appendChild(b);
    });
}

/** Thumbnails are cached on the record. Regenerating a data URL for every page
 *  on every re-render is what made deleting page 9 of 10 feel broken. */
function thumbOf(p) {
    if (!p.thumb && p.out) p.thumb = p.out.toDataURL('image/jpeg', 0.62);
    return p.thumb || '';
}

/* ─── document view ───────────────────────────────────────────────────────── */

function renderGrid() {
    const grid = $('dsGrid');
    grid.innerHTML = '';
    S.pages.forEach((p, i) => {
        const card = document.createElement('div');
        card.className = 'ds-card';
        card.draggable = true;
        card.dataset.i = i;
        card.tabIndex = 0;
        card.setAttribute('role', 'button');
        card.setAttribute('aria-label', T('edit_page').replace('%d', i + 1));

        const img = document.createElement('img');
        img.alt = ''; img.src = thumbOf(p);
        const num = document.createElement('span'); num.className = 'ds-n'; num.textContent = i + 1;
        const del = document.createElement('button');
        del.type = 'button'; del.className = 'ds-x'; del.textContent = '✕';
        del.setAttribute('aria-label', T('remove'));
        del.addEventListener('click', (e) => { e.stopPropagation(); removePage(i); });

        card.append(img, num, del);
        card.addEventListener('click', () => editPage(i));
        card.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); editPage(i); } });
        wireDnd(card);
        grid.appendChild(card);
    });

    const add = document.createElement('button');
    add.type = 'button'; add.className = 'ds-add';
    add.innerHTML = '<span>+</span>' + esc(T('btn_more'));
    add.addEventListener('click', addMore);
    grid.appendChild(add);
}

function wireDnd(el) {
    el.addEventListener('dragstart', () => { el.classList.add('is-drag'); S.drag = +el.dataset.i; });
    el.addEventListener('dragend', () => el.classList.remove('is-drag'));
    el.addEventListener('dragover', (e) => { e.preventDefault(); el.classList.add('is-over'); });
    el.addEventListener('dragleave', () => el.classList.remove('is-over'));
    el.addEventListener('drop', (e) => {
        e.preventDefault(); el.classList.remove('is-over');
        const to = +el.dataset.i;
        if (S.drag < 0 || S.drag === to) return;
        const m = S.pages.splice(S.drag, 1)[0];
        S.pages.splice(to, 0, m);
        S.drag = -1;
        funnel('reorder', { pages: S.pages.length });
        renderGrid();
    });
}

function syncDocControls() {
    const n = S.pages.length;
    $('dsTwoUpWrap').hidden = n < 2;
    if (n < 2) { $('dsTwoUp').checked = false; S.exp.twoUp = false; }
    $('dsPdfOpts').hidden = S.exp.fmt !== 'pdf';
    // JPG for one page, ZIP for several — say which, rather than offering a
    // label that turns out to mean something else once a page is added.
    const imgBtn = $('dsFormats').querySelector('[data-x="img"]');
    if (imgBtn) imgBtn.textContent = n > 1 ? T('fmt_zip') : T('fmt_jpg');
    if (!$('dsName').value) $('dsName').value = defaultName();
    Array.from($('dsFormats').children).forEach((b) => b.classList.toggle('is-on', b.dataset.x === S.exp.fmt));
    Array.from($('dsSizes').children).forEach((b) => b.classList.toggle('is-on', b.dataset.s === S.exp.size));
    Array.from($('dsQuality').children).forEach((b) => b.classList.toggle('is-on', b.dataset.q === S.exp.quality));
    Array.from($('dsFit').children).forEach((b) => b.classList.toggle('is-on', (+b.dataset.f || 0) === S.exp.fitMb));
    Array.from($('dsDpi').children).forEach((b) => b.classList.toggle('is-on', b.dataset.d === S.exp.dpi));
    // The outcome belongs to the file that was made; changing a knob invalidates it.
    const oc = $('dsOutcome'); if (oc) oc.hidden = true;
    $('dsShare').hidden = !canShareFiles();
}

/** "Скан 2026-08-28" beats scan.pdf, scan (1).pdf, scan (2).pdf — which is what
 *  a session of three exports used to leave in the Downloads folder, oldest
 *  first, under identical names. */
function defaultName() {
    const d = new Date();
    const p2 = (x) => String(x).padStart(2, '0');
    return T('doc_title') + ' ' + d.getFullYear() + '-' + p2(d.getMonth() + 1) + '-' + p2(d.getDate());
}
function outName(ext) {
    const ON = window.__outputName;
    const base = ($('dsName').value || '').trim() || defaultName();
    if (ON) return ON.join(ON.sanitize(base), ext);
    return base.replace(/[\\/:*?"<>|]+/g, '-') + '.' + ext;
}

/* ─── page lifecycle ──────────────────────────────────────────────────────── */

function addSource(canvas, fileKey) {
    const p = newPage(canvas);
    S.pages.push(p);
    S.cur = S.pages.length - 1;
    if (!S.docKey) S.docKey = 'doc:' + (fileKey || 'cam-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8));
    openWork();
    S.view = 'page';
    S.tab = 'look';
    render();
    hud(T('detecting'));
    // One place decides what the screen shows, and it runs again when the model
    // refines the frame. Doing it here AND in a trailing call rendered the page
    // twice on every open, veil and all.
    detect(p, (from) => {
        if (S.pages[S.cur] !== p) return;
        p.prevWarp = null; p.out = null; p.thumb = null;
        // Detection failed: open the frame editor and say so, rather than
        // handing back a warped photo of the desk and calling it a scan.
        setTab(from === 'manual' ? 'frame' : S.tab);
        // The status pill is pixels; this is the same news for a screen reader.
        announce(from === 'manual' ? T('no_doc') : T('adjust_hint'));
    });
}

function editPage(i) {
    S.cur = i;
    go('page');
    setTab('look');
}

/**
 * Removing a page is offered back for a few seconds.
 *
 * A page here is not a row in a list — it is a photograph somebody took, and if
 * it came from the camera the only way to get it back is to find the document
 * again and re-shoot it. The × sits on the corner of a thumbnail, next to the
 * thumbnail you tap to EDIT that page, which is exactly the geometry that
 * produces mis-taps: 55 sessions in 30 days removed a page, 151 removals among
 * them. Undo costs one line of state and removes the whole class.
 *
 * The cap is deliberately not refunded and not re-charged: the document keeps
 * its identity while the undo window is open, so removing and restoring a page
 * cannot become a way to start a free second document.
 */
function removePage(i, silent) {
    const gone = silent ? null : S.pages[i];
    S.pages.splice(i, 1);
    funnel('page_removed', { left: S.pages.length });
    const emptied = !S.pages.length;
    if (emptied) {
        // No pages left means the NEXT one starts a new document, so the cap
        // identity has to go with them. Keeping it would make every document
        // after the first free for ever — one unit spent, unlimited scans.
        S.queue = []; lastBlob = null;
        closeWork(); go('doc');
    } else {
        if (S.cur >= S.pages.length) S.cur = S.pages.length - 1;
        render();
    }
    offerUndo(gone, i, emptied ? S.docKey : null);
    if (emptied) S.docKey = null;
}

let undoTimer = 0;
function offerUndo(pageObj, at, keptKey) {
    if (!pageObj) return;
    clearTimeout(undoTimer);
    const bar = $('dsUndo'); if (!bar) return;
    show(bar);
    undoTimer = setTimeout(() => hide(bar), 8000);
    S.undo = { page: pageObj, at: at, key: keptKey };
}

function undoRemove() {
    const u = S.undo; if (!u) return;
    clearTimeout(undoTimer);
    hide($('dsUndo'));
    S.undo = null;
    S.pages.splice(Math.min(u.at, S.pages.length), 0, u.page);
    // A restored first page restores the document it belonged to, cap and all.
    if (u.key && !S.docKey) S.docKey = u.key;
    S.cur = Math.min(u.at, S.pages.length - 1);
    funnel('page_restored', { pages: S.pages.length });
    if (!$('dsWork').classList.contains('is-open')) openWork();
    go('doc');
    render();
}

function commitPage() {
    if (S.queue.length) {
        const next = S.queue.shift();
        addSource(next);
        return;
    }
    go('doc');
}

function addMore() {
    // Camera if that is how this document started, otherwise the file picker —
    // the second side of a passport is almost never a different input method.
    const input = fileInput();
    if (S.cam.lastUsed && navigator.mediaDevices) openCamera();
    else if (input) input.click();
    else openCamera();
}

/* ─── camera ──────────────────────────────────────────────────────────────── */

function capsOf() {
    try { return (S.cam.track && S.cam.track.getCapabilities && S.cam.track.getCapabilities()) || {}; } catch (_) { return {}; }
}

function openCamera() {
    funnel('input', { method: 'camera' });
    S.cam.lastUsed = true;
    openWork();
    go('cam');
    startCamera();
    preloadModel();
}

/**
 * Opening the camera goes through the shared CameraManager, which owns the
 * things this tool used to get wrong on its own: a watchdog for a getUserMedia
 * that never settles, the constraint-relaxation ladder, and — the part that
 * mattered here — CLASSIFYING the refusal into denied / not_found / busy /
 * unresponsive / not_supported and having a sentence for each one in every
 * locale.
 *
 * What it replaced said one of two things ("blocked" or "unavailable") for all
 * six causes. Production, 30 d: 27 sessions failed here, 29 % of every camera
 * attempt, and 21 of them were told to "allow the camera in browser settings" —
 * advice that is simply wrong for a camera another app is holding.
 *
 * Two further rules, both taken from the microphone work of 06.09:
 *  - a "try again" button in the `denied` state cannot work, because the browser
 *    will not re-ask (2868 such calls there, not one prompt). So a retry is
 *    offered only where a prompt is still possible; otherwise the same button
 *    RE-CHECKS the permission, and the stream is picked up on `change`.
 *  - start() resolving is not frames arriving. Without a first-frame watchdog
 *    the user gets a black rectangle under a confident "scanning" badge.
 */
function startCamera() {
    stopCamera();
    hideCamFail();
    if (!S.cam.mgr) S.cam.mgr = new window.CameraManager({ tool: 'document-scanner' });
    if (!S.cam.mgr.isSupported || !S.cam.mgr.isSupported()) {
        showCamFail('not_supported', (S.cam.mgr.getErrorMessage && S.cam.mgr.getErrorMessage('not_supported')) || T('err_no_camera'));
        return;
    }
    S.cam.opening = true;
    S.cam.mgr.start({ facingMode: S.cam.facing, width: 1920, height: 1080 })
        .then((stream) => {
            if (!S.cam.opening) { stream.getTracks().forEach((t) => t.stop()); return; }
            S.cam.opening = false;
            S.cam.stream = stream;
            S.cam.track = stream.getVideoTracks()[0];
            const v = $('dsVideo');
            v.srcObject = stream;
            v.onloadedmetadata = () => { v.play(); render(); setupCamCaps(); camLoop(); };
            stage('camera_opened', { facing: S.cam.facing });
            watchFirstFrame(v);
            keepAwake();
        })
        .catch((err) => {
            S.cam.opening = false;
            const CM = window.CameraManager || {};
            const code = (err && err.code) || (CM.classify ? CM.classify(err) : 'generic');
            const msg = (S.cam.mgr.getErrorMessage && S.cam.mgr.getErrorMessage(code)) || T('err_no_camera');
            diag({ issue: 'camera_failed', name: CM.causeName ? CM.causeName(err) : String((err && err.name) || '?'), code: code });
            showCamFail(code, msg);
        });
}

/** A resolved start() only means the browser handed us a track. Frames are a
 *  separate promise nobody makes: a held device, a privacy shutter or a driver
 *  fault all produce a live track that never paints. Six seconds without a
 *  single frame is a failure the user has to be told about, not a black stage. */
function watchFirstFrame(v) {
    clearTimeout(S.cam.frameT);
    S.cam.frameT = setTimeout(() => {
        if (!S.cam.stream) return;
        if (v.videoWidth > 0) return;
        diag({ issue: 'camera_no_frames' });
        stopCamera();
        showCamFail('unresponsive', T('cam_no_frames'));
    }, 6000);
}

/** The refusal panel. Lives inside the viewfinder, offers the upload door first
 *  (measured: that lane finishes 42 % of the time against the camera's 34 %),
 *  and only offers a retry the browser could actually honour. */
function showCamFail(code, msg) {
    const box = $('dsCamFail'); if (!box) return;
    $('dsCamFailMsg').textContent = msg;
    const how = $('dsCamFailHow');
    const hint = code === 'denied' ? T('camfail_how_denied') : code === 'busy' ? T('camfail_how_busy') : '';
    how.textContent = hint; how.hidden = !hint;
    const retry = $('dsCamFailRetry');
    const stat = $('dsCamFailStat');
    retry.hidden = true; stat.hidden = true;
    show(box);
    hide($('dsVideo'));
    promptStillPossible().then((possible) => {
        // A button whose label is a STATEMENT is not a button. Where the browser
        // will still ask, offer the retry; where it has already decided it will
        // not, say so as a line of text and leave the upload door as the only
        // thing to press.
        if (possible) { retry.textContent = T('camfail_recheck'); retry.hidden = false; }
        else { stat.textContent = T('camfail_still_off'); stat.hidden = false; }
    });
    // Controls that drive a camera that is not running are silent clicks by
    // construction — production logged 20 sessions of them on this tool. While
    // the panel is up, the shutter and the capture settings go away.
    setCamControlsLive(false);
    watchPermissionChange();
    funnel('camera_failed_panel', { code: code });
}

function hideCamFail() {
    const b = $('dsCamFail'); if (b) hide(b);
    const v = $('dsVideo'); if (v && S.view === 'cam') show(v);
    setCamControlsLive(true);
}

/** Shutter + capture settings + torch/switch only exist while a camera does. */
function setCamControlsLive(live) {
    const sh = $('dsShutter'); if (sh) sh.hidden = !live && S.view === 'cam' ? true : sh.hidden;
    if (S.view === 'cam') {
        if (sh) sh.hidden = !live;
        const pane = $('dsPaneCam'); if (pane) pane.hidden = !live;
        const t = $('dsTorch'); if (t && !live) t.hidden = true;
        const sw = $('dsSwitch'); if (sw && !live) sw.hidden = true;
    }
}

function promptStillPossible() {
    return new Promise((resolve) => {
        let done = false;
        const fin = (v) => { if (!done) { done = true; resolve(v); } };
        setTimeout(() => fin(true), 1500);
        try {
            if (!navigator.permissions || !navigator.permissions.query) return fin(true);
            navigator.permissions.query({ name: 'camera' })
                .then((r) => fin(!r || r.state !== 'denied'))
                .catch(() => fin(true));
        } catch (_) { fin(true); }
    });
}

/** Someone who flips the permission in browser settings should not then have to
 *  find a button — the camera just opens. */
function watchPermissionChange() {
    if (S.cam.permWatched) return;
    S.cam.permWatched = true;
    try {
        if (!navigator.permissions || !navigator.permissions.query) return;
        navigator.permissions.query({ name: 'camera' }).then((st) => {
            if (!st) return;
            st.onchange = () => {
                if (st.state === 'granted' && S.view === 'cam') { funnel('camera_recovered', {}); startCamera(); }
            };
        }).catch(() => {});
    } catch (_) {}
}

function stopCamera() {
    if (S.cam.raf) { cancelAnimationFrame(S.cam.raf); S.cam.raf = 0; }
    clearTimeout(S.cam.frameT);
    S.cam.opening = false;
    if (S.cam.mgr && S.cam.mgr.stop) { try { S.cam.mgr.stop(); } catch (_) {} }
    if (S.cam.stream) { S.cam.stream.getTracks().forEach((t) => t.stop()); }
    S.cam.stream = null; S.cam.track = null;
    S.cam.stable = 0; S.cam.quad = null; S.cam.busy = false;
    releaseWake();
}

/** Zoom is a real capability on phones and the reason a scan of a small receipt
 *  comes out readable. Shown only when the track actually reports it. */
function setupCamCaps() {
    const caps = capsOf();
    $('dsTorch').hidden = !caps.torch;
    const zw = $('dsZoomWrap');
    if (caps.zoom && caps.zoom.max > caps.zoom.min) {
        const z = $('dsZoom');
        z.min = caps.zoom.min; z.max = caps.zoom.max;
        z.step = caps.zoom.step || 0.1;
        z.value = (S.cam.track.getSettings && S.cam.track.getSettings().zoom) || caps.zoom.min;
        $('dsZoomOut').textContent = (+z.value).toFixed(1) + '×';
        show(zw);
    } else hide(zw);
    $('dsAuto').checked = S.cam.auto;
}

/** Multi-page scanning is minutes of holding a phone still — exactly the case
 *  the screen dims in the middle of.
 *
 *  Through `window.__wakeLock` rather than the browser API directly (naming it
 *  here would trip the guard that looks for hand-rolled copies): it verifies
 *  the lock is actually HELD rather than merely requested, re-takes it after a
 *  freeze/bfcache restore that delivers no `release` event, falls back to a
 *  dual-codec video where the API is absent, and reports to telemetry. A private
 *  copy — which this was — gets none of that. See docs/wake-lock-failure-modes.md. */
function keepAwake() {
    try { window.__wakeLock && window.__wakeLock.hold('document-scanner-camera'); } catch (_) {}
}
function releaseWake() {
    try { window.__wakeLock && window.__wakeLock.releaseHold('document-scanner-camera'); } catch (_) {}
}

let grabCanvas = null;
function camLoop() {
    S.cam.raf = requestAnimationFrame(camLoop);
    const v = $('dsVideo');
    if (!v.videoWidth) return;
    const ov = $('dsOverlay'), st = $('dsStage');
    if (ov.width !== st.clientWidth || ov.height !== st.clientHeight) {
        ov.width = st.clientWidth; ov.height = st.clientHeight;
    }
    const g = ov.getContext('2d');
    g.clearRect(0, 0, ov.width, ov.height);
    if (S.cam.auto && S.cam.quad) drawCamQuad(g, S.cam.quad, v, ov);
    if (!S.cam.auto) return;

    const now = performance.now();
    if (S.cam.busy || now - S.cam.last < 220) return;
    S.cam.last = now; S.cam.busy = true;
    const gw = 480, gh = Math.round((gw * v.videoHeight) / v.videoWidth);
    if (!grabCanvas) grabCanvas = document.createElement('canvas');
    grabCanvas.width = gw; grabCanvas.height = gh;
    // No frame this tick (an interrupted camera): release busy, or the search never runs again.
    if (!window.__drawCameraFrame(grabCanvas.getContext('2d', { willReadFrequently: true }), v, 0, 0, gw, gh)) {
        S.cam.busy = false;
        return;
    }
    let quad = null;
    try { quad = CVD.detectQuad(rgbaOf(grabCanvas), gw, gh); } catch (_) {}
    S.cam.busy = false;

    if (quad) {
        const sx = v.videoWidth / gw, sy = v.videoHeight / gh;
        quad = quad.map((p) => ({ x: p.x * sx, y: p.y * sy }));
        S.cam.stable = S.cam.quad && quadClose(quad, S.cam.quad) ? S.cam.stable + 1 : 0;
        S.cam.quad = quad;
        hud(S.cam.stable >= 3 ? T('cam_hold') : T('cam_hint'), S.cam.stable >= 3 ? 'good' : null);
        if (S.cam.stable >= 6) { S.cam.stable = 0; capture(true); }
    } else {
        S.cam.quad = null; S.cam.stable = 0;
        hud(T('cam_hint'));
    }
}

function quadClose(a, b) {
    let d = 0;
    for (let i = 0; i < 4; i++) d += dist(a[i], b[i]);
    return d < 90;
}

function drawCamQuad(g, quad, v, ov) {
    const vr = v.videoWidth / v.videoHeight, or = ov.width / ov.height;
    let dw, dh;
    if (vr > or) { dw = ov.width; dh = ov.width / vr; } else { dh = ov.height; dw = ov.height * vr; }
    const ox = (ov.width - dw) / 2, oy = (ov.height - dh) / 2;
    const sx = dw / v.videoWidth, sy = dh / v.videoHeight;
    g.strokeStyle = '#38f58a'; g.lineWidth = 3; g.fillStyle = 'rgba(56,245,138,.12)';
    g.beginPath();
    g.moveTo(ox + quad[0].x * sx, oy + quad[0].y * sy);
    for (let i = 1; i < 4; i++) g.lineTo(ox + quad[i].x * sx, oy + quad[i].y * sy);
    g.closePath(); g.fill(); g.stroke();
}

function capture(auto) {
    const v = $('dsVideo');
    if (!v.videoWidth) return;
    const c = document.createElement('canvas');
    const sc = Math.min(1, MAX_SRC / Math.max(v.videoWidth, v.videoHeight));
    c.width = Math.round(v.videoWidth * sc);
    c.height = Math.round(v.videoHeight * sc);
    if (!window.__drawCameraFrame(c.getContext('2d'), v, 0, 0, c.width, c.height)) {
        // the camera stays on: the next press a second later gets its frame
        if (!auto && window.__toast) window.__toast(window.__cameraNoFrame(), 'warning');
        return;
    }
    stopCamera();
    hud(null);
    stage('captured', { auto: !!auto });
    addSource(c);
}

/* ─── input ───────────────────────────────────────────────────────────────── */

function preloadModel() {
    const t0 = performance.now();
    MLD.preload(CFG.onnx, onModelProgress)
        .then(() => stage('model_loaded', { ms: Math.round(performance.now() - t0) }))
        .catch((e) => diag({ issue: 'model_load_failed', msg: String((e && e.message) || e).slice(0, 80) }));
}
function onModelProgress(recv, total) {
    if (!total || $('dsVeil').hidden) return;
    $('dsVeilTxt').textContent = T('model_dl') + ' ' + Math.round((recv / total) * 100) + '%';
}

function fileToCanvas(file) {
    return new Promise((resolve, reject) => {
        const done = (bmp, w, h) => {
            const sc = Math.min(1, MAX_SRC / Math.max(w, h));
            const c = document.createElement('canvas');
            c.width = Math.round(w * sc); c.height = Math.round(h * sc);
            c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
            if (bmp.close) { try { bmp.close(); } catch (_) {} }
            resolve(c);
        };
        const fallback = () => {
            const url = URL.createObjectURL(file);
            const im = new Image();
            im.onload = () => { URL.revokeObjectURL(url); done(im, im.naturalWidth, im.naturalHeight); };
            im.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')); };
            im.src = url;
        };
        if (window.createImageBitmap) {
            createImageBitmap(file, { imageOrientation: 'from-image' })
                .then((b) => done(b, b.width, b.height))
                .catch(fallback);
        } else fallback();
    });
}

function takeFiles(files) {
    if (!files || !files.length) return;
    const list = Array.prototype.slice.call(files);
    funnel('input', { method: 'upload', count: list.length, pdf: list.filter(isPdf).length });
    S.cam.lastUsed = false;
    preloadModel();
    const key = window.__toolLimit ? window.__toolLimit.fileKey(list[0]) : null;
    S.queue = [];
    let first = true;
    const take = (c) => { if (first) { first = false; addSource(c, key); } else S.queue.push(c); };
    list.reduce((chain, f) => chain.then(() =>
        (isPdf(f) ? pdfToCanvases(f) : fileToCanvas(f).then((c) => [c])).then((cs) => cs.forEach(take))
    ), Promise.resolve())
        .then(() => { if (!first) render(); else toast(T('err_decode'), 'error'); })
        .catch((e) => {
            const m = String((e && e.message) || e);
            diag({ issue: 'input_failed', msg: m.slice(0, 80) });
            toast(m === 'pdf-empty' ? T('err_pdf_empty') : m === 'pdf-locked' ? T('err_pdf_locked') : T('err_decode'), 'error');
        });
}

function isPdf(f) {
    return !!f && (f.type === 'application/pdf' || /\.pdf$/i.test(f.name || ''));
}

/**
 * A scan people already have is usually a PDF, not a JPG — a crooked page out
 * of an office MFU, or somebody's phone-app scan («скан документа pdf» 1 182
 * shows/mo, «отсканированные документы pdf» 2 232). Refusing it meant the one
 * tool on the site that can straighten and clean a page could not open the
 * commonest container that page arrives in.
 *
 * Pages are rasterised at a scale that lands the long edge near MAX_SRC: below
 * that the text softens before the enhancement stage ever sees it, above it we
 * would just be carrying pixels the export throws away.
 */
function pdfToCanvases(file) {
    return ensurePdfjs().then((pdfjs) => file.arrayBuffer().then((buf) => {
        progress(0.05, T('pdf_reading'));
        return pdfjs.getDocument({ data: new Uint8Array(buf), isEvalSupported: false }).promise;
    })).then((doc) => {
        const n = Math.min(doc.numPages, PDF_MAX_PAGES);
        if (!n) throw new Error('pdf-empty');
        if (doc.numPages > PDF_MAX_PAGES) toast(T('pdf_truncated').replace('%d', String(PDF_MAX_PAGES)), 'warning');
        const out = [];
        let chain = Promise.resolve();
        for (let i = 1; i <= n; i++) {
            chain = chain.then(() => doc.getPage(i)).then((pg) => {
                const base = pg.getViewport({ scale: 1 });
                const sc = Math.min(4, Math.max(1, MAX_SRC / Math.max(base.width, base.height)));
                const vp = pg.getViewport({ scale: sc });
                const c = document.createElement('canvas');
                c.width = Math.round(vp.width); c.height = Math.round(vp.height);
                progress(0.05 + 0.9 * (i / n), T('pdf_page').replace('%d', String(i)));
                return pg.render({ canvasContext: c.getContext('2d'), viewport: vp }).promise.then(() => { out.push(c); });
            });
        }
        return chain.then(() => { progress(0); stage('pdf_imported', { pages: out.length }); return out; });
    }).catch((e) => {
        progress(0);
        const m = String((e && e.message) || e);
        if (/password/i.test(m)) throw new Error('pdf-locked');
        throw e;
    });
}

function ensurePdfjs() {
    if (pdfjsP) return pdfjsP;
    pdfjsP = import('/vendor/pdfjs/4/pdf.min.mjs').then((mod) => {
        mod.GlobalWorkerOptions.workerSrc = '/vendor/pdfjs/4/pdf.worker.min.mjs';
        return mod;
    }).catch((e) => { pdfjsP = null; throw e; });
    return pdfjsP;
}

/* ─── OCR ─────────────────────────────────────────────────────────────────── */

function loadScriptChain(srcs, i) {
    return new Promise((resolve, reject) => {
        if (window.Tesseract) return resolve(window.Tesseract);
        if (i >= srcs.length) return reject(new Error('tesseract-load'));
        const s = document.createElement('script');
        s.src = srcs[i]; s.async = true;
        s.onload = () => (window.Tesseract ? resolve(window.Tesseract) : loadScriptChain(srcs, i + 1).then(resolve, reject));
        s.onerror = () => { s.remove(); loadScriptChain(srcs, i + 1).then(resolve, reject); };
        document.head.appendChild(s);
    });
}

/**
 * Recognition goes through the shared OCR door (public/js/ocr/ocr-engine.js):
 * PP-OCRv5 in a worker for the scripts it knows, Tesseract for the rest —
 * measured 12.09.2026 on the tests/ocr corpora (1.5 % vs 11 % CER; phone pages
 * 1–9 % vs 25–100 %). The scanner hands over pages it has already straightened
 * and cropped, so neither orientation nor deskew is asked for here. The
 * engine's data has Tesseract's shape (text, words with bbox + confidence).
 */
function ensureOcr() {
    if (ocrWorker) return Promise.resolve(ocrWorker);
    progress(0.04, T('ocr_running'));
    const cfg = CFG.tess || {};
    const lang = CFG.ocrLang === 'eng' ? 'eng' : CFG.ocrLang + '+eng';
    if (!window.OcrEngine) return Promise.reject(new Error('OcrEngine missing'));
    const srcs = cfg.sources || [];
    return window.OcrEngine.open(lang, {
        slug: 'document-scanner',
        workerUrl: CFG.ppocrWorker || window.__PPOCR_WORKER_URL || undefined,
        tesseractUrl: srcs[0], tesseractFallbackUrl: srcs[1],
        tesseract: { langPath: cfg.langPath, workerPath: cfg.workerPath, corePath: cfg.corePath },
        onProgress: (f) => progress(0.04 + 0.1 * f, T('ocr_running')),
    }).then((eng) => { ocrWorker = eng; return eng; });
}

function ocrWords(engine, canvas) {
    return engine.recognize(canvas)
        .then((d) => ((d && d.words) || []).filter((w) => w.confidence > 30 && w.text && w.text.trim()))
        .catch(() => []);
}

/**
 * The long edge, in pixels, that every page of this export is rendered to.
 *
 * Three things bound it and the smallest wins, which is the whole point:
 *   · what was ASKED for — the dpi choice against the chosen sheet;
 *   · what the PHOTO HAS — the longest edge of the selected quad in source
 *     pixels. Rendering beyond that is interpolation, and a page interpolated
 *     up to 300 dpi is not a 300 dpi scan, it is a 150 dpi scan with a bigger
 *     file and a false claim attached;
 *   · what the DEVICE can hold — a 300 dpi A4 canvas is 8.7 Mpx and about
 *     35 MB, and every page of the document exists at once while the PDF is
 *     assembled. On a 4 GB phone a ten-page document at 300 dpi is 350 MB of
 *     canvas, which is how a tab dies without an error.
 *
 * Whatever comes out, the achieved density is measured afterwards and shown.
 */
function exportEdge() {
    const dpi = DPI_CHOICES[S.exp.dpi] || DPI_CHOICES.normal;
    const sheetIn = S.exp.size === 'letter' ? 11 : 11.693;
    let want = S.exp.size === 'fit' ? Math.round(dpi * 11.693) : Math.round(dpi * sheetIn);

    // What the photos actually contain.
    let haveMax = 0;
    for (const p of S.pages) {
        const q = p.quad;
        const side = (a, b) => Math.hypot(q[b].x - q[a].x, q[b].y - q[a].y);
        haveMax = Math.max(haveMax, side(0, 1), side(1, 2), side(2, 3), side(3, 0));
    }
    if (haveMax > 0) want = Math.min(want, Math.round(haveMax));

    // What the device can hold, across the whole document.
    const n = Math.max(1, S.pages.length);
    const perPage = PIXEL_BUDGET / n;
    const aspect = 0.707;                       // A4-ish; short edge / long edge
    const byMem = Math.round(Math.sqrt(perPage / aspect));
    if (byMem < want) {
        want = Math.max(900, byMem);
        stage('dpi_capped_by_memory', { pages: n, edge: want });
    }
    return Math.max(900, want);
}

/** What each quality choice actually costs, measured 2026-08-28 on a 1400×1980
 *  enhanced A4 page (flat white, black text, light sensor noise — what this
 *  tool really emits):
 *
 *      high    702 KB   1.63x
 *      normal  432 KB   1.00x
 *      small   152 KB   0.35x
 *
 *  So "small" is a third of the size, not a rounding difference — it exists for
 *  the mail attachment that bounced, and it is worth having for that alone. */
const JPEG_Q = { high: 0.92, normal: 0.85, small: 0.62 };

/* ─── export ──────────────────────────────────────────────────────────────── */

/**
 * Full-resolution pass over every page.
 *
 * Yields to the browser between pages, and that is the whole point: measured
 * 2026-08-28, one 1800×2400 capture costs 537 ms at 1900 px (warp 144, deskew
 * 42, Sauvola 351). Ten pages is 5.4 seconds. Run as a plain loop the tab is
 * frozen for all of it and the progress bar cannot paint a single frame — a
 * progress bar that is structurally unable to move is worse than none, because
 * it looks like the tool has died.
 */
async function exportPagesFull() {
    const out = [];
    const edge = exportEdge();
    for (let i = 0; i < S.pages.length; i++) {
        progress(0.05 + 0.35 * (i / S.pages.length), T('processing'));
        // Two frames: one to paint the bar, one to be sure it landed.
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
        out.push(renderFull(S.pages[i], edge));
    }
    return S.exp.twoUp ? pairUp(out, { dpi: DPI_CHOICES[S.exp.dpi] || DPI_CHOICES.normal }) : out;
}

function runExport() {
    if (!S.pages.length) { toast(T('err_no_pages'), 'warning'); return; }
    const TL = window.__toolLimit;
    const run = TL ? TL.begin(SLUG, S.docKey ? [S.docKey] : null) : { commit() {}, abort() {} };
    if (!run) return;   // the shared note is already on screen

    const fmt = S.exp.fmt;
    $('dsCta').disabled = true;
    progress(0.02, T('exporting'));

    const work = fmt === 'txt' ? exportText() : fmt === 'img' ? exportImages() : fmt === 'docx' ? exportDocx() : exportPdf();
    work.then((meta) => {
        run.commit();
        progressDone();
        $('dsCta').disabled = false;
        complete(Object.assign({ pages: S.pages.length }, meta || {}));
    }).catch((e) => {
        run.abort();
        progressDone();
        $('dsCta').disabled = false;
        const msg = String((e && e.message) || e);
        // 'handled' means the failing step already said something specific and
        // actionable; a generic "could not build the file" on top of it would
        // only bury the sentence that told the user what to do.
        if (msg === 'handled') return;
        toast(msg === 'no-text' ? T('no_text') : T('err_export'), msg === 'no-text' ? 'warning' : 'error');
        if (msg !== 'no-text') diag({ issue: 'export_failed', fmt, msg: msg.slice(0, 90) });
    });
}

let lastBlob = null;
async function exportPdf() {
    const pages = await exportPagesFull();
    const useOcr = S.exp.ocr;
    const started = performance.now();
    const step = () => Promise.resolve();
    const target = S.exp.fitMb ? Math.round(S.exp.fitMb * 1024 * 1024) : 0;
    return (useOcr ? ensureOcr() : step()).then((worker) => buildPdf(pages, {
        size: S.exp.size,
        quality: JPEG_Q[S.exp.quality] || JPEG_Q.normal,
        targetBytes: target,
        title: ($('dsName').value || '').trim() || defaultName(),
        ocr: useOcr ? ((canvas, i) => { progress(0.45 + 0.5 * (i / pages.length), T('ocr_page').replace('%d', i + 1)); return ocrWords(worker, canvas); }) : null,
        onProgress: (f) => progress(0.45 + 0.5 * f, useOcr ? null : T('exporting')),
    })).then((blob) => {
        lastBlob = { blob, name: outName('pdf') };
        window.__downloadFile(blob, lastBlob.name);
        const dpi = effectiveDpi(pages[0]);
        reportOutcome(blob.size, dpi, target);
        toast(withSize(T('done_pdf'), blob), 'success');
        return {
            format: 'pdf', ocr: useOcr, size: S.exp.size, dpi: dpi,
            fit: S.exp.fitMb || 0, bytes: blob.size,
            fitMissed: target && blob.size > target ? 1 : 0,
            ms: Math.round(performance.now() - started),
        };
    });
}

/**
 * The resolution the exported sheet really carries.
 *
 * Not a setting — an outcome. A phone photo has as many pixels as it has, and
 * spreading them over an A4 sheet is what fixes the density; upscaling to be
 * able to print "300 dpi" would be a lie told in pixels. Forms that cap the
 * file size usually also demand 150-300 dpi, and the dpi requirement is the
 * stricter one, so this is the number worth showing next to the megabytes.
 */
function effectiveDpi(page) {
    if (!page) return 0;
    const inches = S.exp.size === 'letter' ? { w: 8.5, h: 11 } : { w: 8.268, h: 11.693 };
    const landscape = page.w > page.h;
    const across = landscape ? inches.h : inches.w;
    return Math.round(page.w / across);
}

/** Says what came out, in the two units the receiving form cares about. */
function reportOutcome(bytes, dpi, target) {
    const el = $('dsOutcome'); if (!el) return;
    const mb = bytes / 1048576;
    const sz = mb >= 1 ? mb.toFixed(1) + ' ' + T('mb') : Math.round(bytes / 1024) + ' ' + T('kb');
    let txt = T('outcome').replace('%s', sz);
    if (dpi && S.exp.size !== 'fit') txt += ' · ' + T('outcome_dpi').replace('%d', String(dpi));
    el.textContent = txt;
    el.hidden = false;
    // Missing a budget the user set has to be said out loud, not hidden behind a
    // number they would have to compare themselves.
    if (target && bytes > target) {
        toast(T('fit_missed').replace('%s', sz), 'warning');
        funnel('fit_missed', { want: S.exp.fitMb, got: Math.round(bytes / 1048576 * 10) / 10 });
    }
    if (dpi && dpi < 150 && S.exp.size !== 'fit') toast(T('dpi_low').replace('%d', String(dpi)), 'warning');
}

/**
 * Word, because that is the word people use: «отсканированный документ в ворд»
 * 1 435 shows/mo, and it is the format an office asks for when it wants to edit
 * what you scanned. Each page goes in as a full-width picture; with the text
 * layer switched on the recognised text goes in above it, so the document is
 * both readable and editable rather than a picture pretending to be a document.
 */
async function exportDocx() {
    const pages = await exportPagesFull();
    const started = performance.now();
    const useOcr = S.exp.ocr;
    let texts = null;
    if (useOcr) {
        const engine = await ensureOcr();
        texts = [];
        for (let i = 0; i < pages.length; i++) {
            progress(0.45 + 0.4 * (i / pages.length), T('ocr_page').replace('%d', i + 1));
            const d = await engine.recognize(pages[i].canvas).catch(() => null);
            texts.push((((d && d.text) || '')).trim());
        }
    }
    progress(0.9, T('exporting'));
    const imgs = [];
    for (const p of pages) {
        const b = await new Promise((res, rej) => p.canvas.toBlob((x) => (x ? res(x) : rej(new Error('encode'))), 'image/jpeg', JPEG_Q[S.exp.quality] || JPEG_Q.normal));
        imgs.push({ bytes: new Uint8Array(await b.arrayBuffer()), w: p.w, h: p.h });
    }
    const blob = await buildDocx(imgs, texts);
    lastBlob = { blob, name: outName('docx') };
    window.__downloadFile(blob, lastBlob.name);
    reportOutcome(blob.size, 0, 0);
    toast(withSize(T('done_pdf'), blob), 'success');
    return { format: 'docx', ocr: useOcr, pagesN: pages.length, bytes: blob.size, ms: Math.round(performance.now() - started) };
}

/**
 * A .docx is a ZIP of XML parts, and the four written here are the minimum a
 * word processor will open: the content-types map, the package relationship to
 * the document, the document's own relationships to the images, and the body.
 * Written directly rather than through a library because the document is this
 * simple — one picture per page — and pulling a writer in would cost more bytes
 * on the wire than the feature.
 */
async function buildDocx(imgs, texts) {
    const enc = new TextEncoder();
    const files = [];
    const EMU = 914400;                       // English Metric Units per inch
    const pageW = 6.0;                        // usable width on A4 with default margins
    let body = '';
    imgs.forEach((im, i) => {
        if (texts && texts[i]) {
            for (const para of String(texts[i]).split(/\n{1,}/)) {
                if (para.trim()) body += '<w:p><w:r><w:t xml:space="preserve">' + xmlEsc(para) + '</w:t></w:r></w:p>';
            }
        }
        const cx = Math.round(pageW * EMU);
        const cy = Math.round(pageW * (im.h / im.w) * EMU);
        body += '<w:p><w:r><w:drawing><wp:inline distT="0" distB="0" distL="0" distR="0">'
            + '<wp:extent cx="' + cx + '" cy="' + cy + '"/><wp:docPr id="' + (i + 1) + '" name="Scan ' + (i + 1) + '"/>'
            + '<a:graphic xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"><a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture">'
            + '<pic:pic xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture">'
            + '<pic:nvPicPr><pic:cNvPr id="' + (i + 1) + '" name="Scan ' + (i + 1) + '"/><pic:cNvPicPr/></pic:nvPicPr>'
            + '<pic:blipFill><a:blip r:embed="rId' + (i + 10) + '"/><a:stretch><a:fillRect/></a:stretch></pic:blipFill>'
            + '<pic:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="' + cx + '" cy="' + cy + '"/></a:xfrm>'
            + '<a:prstGeom prst="rect"><a:avLst/></a:prstGeom></pic:spPr></pic:pic>'
            + '</a:graphicData></a:graphic></wp:inline></w:drawing></w:r></w:p>';
        if (i < imgs.length - 1) body += '<w:p><w:r><w:br w:type="page"/></w:r></w:p>';
    });

    files.push(['[Content_Types].xml', enc.encode('<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">'
        + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
        + '<Default Extension="xml" ContentType="application/xml"/>'
        + '<Default Extension="jpeg" ContentType="image/jpeg"/>'
        + '<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>'
        + '</Types>')]);
    files.push(['_rels/.rels', enc.encode('<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
        + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>'
        + '</Relationships>')]);
    let rels = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">';
    imgs.forEach((im, i) => {
        rels += '<Relationship Id="rId' + (i + 10) + '" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="media/scan' + (i + 1) + '.jpeg"/>';
        files.push(['word/media/scan' + (i + 1) + '.jpeg', im.bytes]);
    });
    rels += '</Relationships>';
    files.push(['word/_rels/document.xml.rels', enc.encode(rels)]);
    files.push(['word/document.xml', enc.encode('<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"'
        + ' xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"'
        + ' xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing">'
        + '<w:body>' + body + '<w:sectPr><w:pgSz w:w="11906" w:h="16838"/>'
        + '<w:pgMar w:top="1134" w:right="850" w:bottom="1134" w:left="1701"/></w:sectPr></w:body></w:document>')]);

    return zipStore(files, 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
}

function xmlEsc(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

/** A stored (uncompressed) ZIP. JPEGs are already compressed and the XML parts
 *  are a few kilobytes, so deflate would buy nothing and cost a dependency. */
function zipStore(files, mime) {
    const enc = new TextEncoder();
    const parts = [], central = [];
    let offset = 0;
    for (const [name, data] of files) {
        const nameBytes = enc.encode(name);
        const crc = crc32(data);
        const lh = new DataView(new ArrayBuffer(30));
        lh.setUint32(0, 0x04034b50, true); lh.setUint16(4, 20, true); lh.setUint16(6, 0x0800, true);
        lh.setUint16(8, 0, true); lh.setUint16(10, 0, true); lh.setUint16(12, 0, true);
        lh.setUint32(14, crc, true); lh.setUint32(18, data.length, true); lh.setUint32(22, data.length, true);
        lh.setUint16(26, nameBytes.length, true); lh.setUint16(28, 0, true);
        parts.push(new Uint8Array(lh.buffer), nameBytes, data);
        const ch = new DataView(new ArrayBuffer(46));
        ch.setUint32(0, 0x02014b50, true); ch.setUint16(4, 20, true); ch.setUint16(6, 20, true);
        ch.setUint16(8, 0x0800, true); ch.setUint16(10, 0, true);
        ch.setUint32(16, crc, true); ch.setUint32(20, data.length, true); ch.setUint32(24, data.length, true);
        ch.setUint16(28, nameBytes.length, true); ch.setUint32(42, offset, true);
        central.push(new Uint8Array(ch.buffer), nameBytes);
        offset += 30 + nameBytes.length + data.length;
    }
    let cdSize = 0;
    for (const c of central) cdSize += c.length;
    const end = new DataView(new ArrayBuffer(22));
    end.setUint32(0, 0x06054b50, true);
    end.setUint16(8, files.length, true); end.setUint16(10, files.length, true);
    end.setUint32(12, cdSize, true); end.setUint32(16, offset, true);
    return new Blob([...parts, ...central, new Uint8Array(end.buffer)], { type: mime });
}

let CRC_T = null;
function crc32(buf) {
    if (!CRC_T) {
        CRC_T = new Uint32Array(256);
        for (let i = 0; i < 256; i++) {
            let c = i;
            for (let k = 0; k < 8; k++) c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
            CRC_T[i] = c >>> 0;
        }
    }
    let c = 0xFFFFFFFF;
    for (let i = 0; i < buf.length; i++) c = CRC_T[(c ^ buf[i]) & 0xFF] ^ (c >>> 8);
    return (c ^ 0xFFFFFFFF) >>> 0;
}

/**
 * Printing is the other half of "I need a scan": «как отсканировать документ на
 * принтере» is 8 925 shows/mo, and a fair share of that is somebody who has the
 * file and needs it on paper. Printed from a hidden frame so the tool's own
 * chrome — rail, dock, overlay — is not what comes out of the printer.
 */
function printPages() {
    if (!S.pages.length) { toast(T('err_no_pages'), 'warning'); return; }
    funnel('print', { pages: S.pages.length });
    progress(0.1, T('printing'));
    exportPagesFull().then((pages) => {
        const html = pages.map((p) => '<img src="' + p.canvas.toDataURL('image/jpeg', 0.9) + '">').join('');
        const fr = document.createElement('iframe');
        fr.setAttribute('aria-hidden', 'true');
        fr.style.cssText = 'position:fixed;left:-10000px;top:0;width:1px;height:1px;border:0';
        document.body.appendChild(fr);
        const d = fr.contentDocument;
        d.open();
        d.write('<!doctype html><meta charset="utf-8"><style>'
            + '@page{margin:8mm}html,body{margin:0;padding:0}'
            + 'img{display:block;width:100%;page-break-after:always;break-after:page}'
            + 'img:last-child{page-break-after:auto;break-after:auto}</style>' + html);
        d.close();
        progressDone();
        const go = () => { try { fr.contentWindow.focus(); fr.contentWindow.print(); } catch (_) {}
            setTimeout(() => fr.remove(), 60000); };
        if (d.readyState === 'complete') setTimeout(go, 250); else fr.onload = () => setTimeout(go, 250);
    }).catch(() => { progressDone(); toast(T('err_export'), 'error'); });
}

async function exportImages() {
    const pages = await exportPagesFull();
    if (pages.length === 1) {
        return new Promise((res, rej) => {
            pages[0].canvas.toBlob((b) => {
                if (!b) return rej(new Error('encode'));
                lastBlob = { blob: b, name: outName('jpg') };
                window.__downloadFile(b, lastBlob.name);
                toast(withSize(T('done_imgs'), b), 'success');
                res({ format: 'jpg' });
            }, 'image/jpeg', JPEG_Q[S.exp.quality] || JPEG_Q.normal);
        });
    }
    // window.__batchDeliver, not JSZip.generateAsync: holding the results AND the
    // archive at once tops out around 2 GiB of live blob bytes (1.6 GB on an
    // 8 GB machine). Past that the archive builds, the download reports
    // "saved", and there is nothing on disk. The primitive refuses BEFORE the
    // work instead, which is the only answer a user can act on.
    const BD = window.__batchDeliver;
    if (!BD || !BD.packZip) return Promise.reject(new Error('batch-deliver-missing'));
    const used = new Set();
    const ON = window.__outputName;
    const base = ($('dsName').value || '').trim() || defaultName();
    return pages.reduce((chain, p, i) => chain.then((items) => new Promise((res) => {
        p.canvas.toBlob((b) => {
            let n = base + ' ' + String(i + 1).padStart(2, '0') + '.jpg';
            if (ON) n = ON.distinct(ON.join(ON.sanitize(base + ' ' + String(i + 1).padStart(2, '0')), 'jpg'), used);
            used.add(n);
            items.push({ name: n, blob: b });
            progress(0.45 + 0.35 * ((i + 1) / pages.length), T('exporting'));
            res(items);
        }, 'image/jpeg', JPEG_Q[S.exp.quality] || JPEG_Q.normal);
    })), Promise.resolve([]))
        // packZip reports (index, total) — not a fraction. Treating the index as
        // one would send the bar to 100% on the first file and sit there.
        .then((items) => BD.packZip(items, (i, n) => progress(0.8 + 0.2 * (n ? i / n : 0), T('exporting'))))
        .then((b) => {
            lastBlob = { blob: b, name: outName('zip') };
            window.__downloadFile(b, lastBlob.name);
            toast(withSize(T('done_imgs'), b), 'success');
            return { format: 'zip' };
        })
        .catch((e) => {
            if (e && e.code === 'BATCH_TOO_BIG') {
                toast(T('err_too_big'), 'error');
                diag({ issue: 'batch_too_big', bytes: e.totalBytes, ceiling: e.ceilingBytes });
                throw new Error('handled');
            }
            throw e;
        });
}

async function exportText() {
    const pages = await exportPagesFull();
    return ensureOcr().then((engine) => pages.reduce((chain, p, i) => chain.then((acc) => {
        progress(0.45 + 0.5 * (i / pages.length), T('ocr_page').replace('%d', i + 1));
        return engine.recognize(p.canvas).then((d) => {
            const txt = (((d && d.text) || '')).trim();
            return acc + (acc && txt ? '\n\n' : '') + txt;
        });
    }), Promise.resolve(''))).then((text) => {
        text = (text || '').trim();
        if (!text) throw new Error('no-text');
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
        lastBlob = { blob, name: outName('txt') };
        // Clipboard first — the reason to OCR a scan is usually to paste it —
        // with the file as the fallback, never instead of a result.
        const save = () => window.__downloadFile(blob, lastBlob.name);
        if (navigator.clipboard && navigator.clipboard.writeText) {
            return navigator.clipboard.writeText(text)
                .then(() => { toast(T('copied'), 'success'); return { format: 'text' }; })
                .catch(() => { save(); toast(T('done_imgs'), 'success'); return { format: 'text' }; });
        }
        save();
        return { format: 'text' };
    });
}

/* ─── share ───────────────────────────────────────────────────────────────── */

function canShareFiles() {
    try {
        return !!(navigator.canShare && navigator.share &&
            navigator.canShare({ files: [new File([new Blob(['x'])], 'a.pdf', { type: 'application/pdf' })] }));
    } catch (_) { return false; }
}

/** A scan made on a phone is nearly always on its way to somebody: a bank, a
 *  landlord, a chat. Handing back a file in Downloads and stopping there is
 *  where the job actually ends on desktop and only half-ends on mobile. */
function shareDoc() {
    const send = (b) => {
        const file = new File([b.blob], b.name, { type: b.blob.type });
        return navigator.share({ files: [file], title: b.name })
            .then(() => { toast(T('shared'), 'success'); funnel('shared', { fmt: S.exp.fmt }); })
            .catch((e) => { if (e && e.name !== 'AbortError') toast(T('err_share'), 'error'); });
    };
    if (lastBlob) return send(lastBlob);
    // Nothing built yet — build it, then share, without a second press.
    const TL = window.__toolLimit;
    const run = TL ? TL.begin(SLUG, S.docKey ? [S.docKey] : null) : { commit() {}, abort() {} };
    if (!run) return Promise.resolve();
    progress(0.02, T('exporting'));
    const built = S.exp.fmt === 'img' ? exportImages() : S.exp.fmt === 'txt' ? exportText() : exportPdf();
    return built.then(() => { run.commit(); progressDone(); return lastBlob ? send(lastBlob) : null; })
        .catch(() => { run.abort(); progressDone(); toast(T('err_export'), 'error'); });
}

/* ─── frame dragging ──────────────────────────────────────────────────────── */

function framePointer(ev, type) {
    if (S.view !== 'page' || S.tab !== 'frame') return;
    const p = S.pages[S.cur]; if (!p) return;
    const c = $('dsCanvas'), r = c.getBoundingClientRect();
    const x = ((ev.clientX - r.left) * (c.width / r.width)) / S.dispScale;
    const y = ((ev.clientY - r.top) * (c.height / r.height)) / S.dispScale;

    if (type === 'down') {
        let best = -1, bd = Infinity;
        for (let i = 0; i < 4; i++) { const d = dist({ x, y }, p.quad[i]); if (d < bd) { bd = d; best = i; } }
        S.corner = bd < 44 / S.dispScale ? best : -1;
        if (S.corner >= 0) { ev.preventDefault(); showLoupe(S.corner); }
    } else if (type === 'move' && S.corner >= 0) {
        ev.preventDefault();
        p.quad[S.corner] = { x: Math.max(0, Math.min(p.src.width, x)), y: Math.max(0, Math.min(p.src.height, y)) };
        if (!p.touched) { p.touched = true; funnel('manual_adjust', { src: p.from }); }
        p.prevWarp = null; p.out = null; p.thumb = null;
        drawFrame(); showLoupe(S.corner);
    } else if (type === 'up') { S.corner = -1; hideLoupe(); }
}

/* ─── wiring ──────────────────────────────────────────────────────────────── */

function bindUploader() {
    const el = $('dsUpload');
    if (!el) return;
    const handler = (files) => takeFiles(files);
    if (typeof window.__bindUploaderFile === 'function') {
        // The shared helper also drains a file picked before this module ran —
        // a real race on a slow network, since a module script is deferred.
        window.__bindUploaderFile(el, (f, d) => handler((d && d.files) || (f ? [f] : [])));
    } else {
        el.addEventListener('tool-uploader:file', (e) => handler((e.detail && (e.detail.files || (e.detail.file && [e.detail.file]))) || []));
    }
    const input = el.querySelector('input[type=file]');
    const door = $('dsPickDoor');
    if (door && input) door.addEventListener('click', () => input.click());
}

/** The file input the entry and "another page" both open. */
function fileInput() {
    const el = $('dsUpload');
    return el && el.querySelector('input[type=file]');
}

function init() {
    bindUploader();

    $('dsCamOpen').addEventListener('click', openCamera);

    // Camera refusal panel. The upload button is the loud one on purpose: it is
    // the lane that finishes, and it works no matter which of the six causes
    // stopped the camera.
    $('dsCamFailPick').addEventListener('click', () => {
        funnel('camera_failed_to_upload', {});
        stopCamera(); hideCamFail();
        S.pages.length ? go('doc') : closeWork();
        const inp = fileInput(); if (inp) inp.click();
    });
    $('dsCamFailRetry').addEventListener('click', () => {
        promptStillPossible().then((possible) => {
            if (possible) { startCamera(); return; }
            // Still switched off: say so plainly rather than re-asking a browser
            // that has already decided it will not ask.
            toast(T('camfail_still_off'), 'warning');
        });
    });

    // top bar
    $('dsBack').addEventListener('click', () => {
        if (S.view === 'page') { commitPage(); }
        else if (S.view === 'cam') { stopCamera(); S.pages.length ? go('doc') : closeWork(); }
        else closeWork();
    });
    $('dsClose').addEventListener('click', () => { stopCamera(); closeWork(); });
    $('dsRotate').addEventListener('click', () => {
        const p = S.pages[S.cur]; if (!p) return;
        p.rot = (p.rot + 90) % 360; p.thumb = null;
        if (S.tab === 'look') renderLook(); else drawFrame();
    });
    $('dsSwitch').addEventListener('click', () => {
        S.cam.facing = S.cam.facing === 'environment' ? 'user' : 'environment';
        startCamera();
    });
    $('dsTorch').addEventListener('click', () => {
        if (!S.cam.track) return;
        S.cam.torch = !S.cam.torch;
        S.cam.track.applyConstraints({ advanced: [{ torch: S.cam.torch }] })
            .then(() => $('dsTorch').classList.toggle('is-on', S.cam.torch))
            .catch(() => {});
    });

    // camera pane
    $('dsAuto').addEventListener('change', () => { S.cam.auto = $('dsAuto').checked; setPref('autocapture', S.cam.auto); if (!S.cam.auto) hud(T('cam_hint')); });
    $('dsZoom').addEventListener('input', () => {
        const z = +$('dsZoom').value;
        $('dsZoomOut').textContent = z.toFixed(1) + '×';
        if (S.cam.track) S.cam.track.applyConstraints({ advanced: [{ zoom: z }] }).catch(() => {});
    });
    $('dsShutter').addEventListener('click', () => capture(false));

    // page pane
    $('dsPageTabs').addEventListener('click', (e) => {
        const b = e.target.closest('[data-pt]'); if (!b) return;
        setTab(b.dataset.pt);
    });
    $('dsFilters').addEventListener('click', (e) => {
        const b = e.target.closest('[data-f]'); if (!b) return;
        const p = S.pages[S.cur]; if (!p) return;
        p.filter = b.dataset.f; setPref('mode', p.filter);
        funnel('mode', { mode: p.filter });
        syncPageControls();
        renderLook();
    });
    let sliderT = 0;
    const onSlider = () => {
        const p = S.pages[S.cur]; if (!p) return;
        p.bright = parseInt($('dsBright').value, 10) || 0;
        p.contrast = (parseInt($('dsContrast').value, 10) || 100) / 100;
        $('dsBrightOut').textContent = p.bright;
        $('dsContrastOut').textContent = Math.round(p.contrast * 100);
        clearTimeout(sliderT);
        sliderT = setTimeout(() => renderLook(), 180);
    };
    $('dsBright').addEventListener('input', onSlider);
    $('dsContrast').addEventListener('input', onSlider);
    $('dsDeskew').addEventListener('change', () => {
        const p = S.pages[S.cur]; if (!p) return;
        p.deskew = $('dsDeskew').checked; setPref('deskew', p.deskew);
        p.prevWarp = null; renderLook();
    });
    $('dsDetectAgain').addEventListener('click', () => {
        const p = S.pages[S.cur]; if (!p) return;
        p.touched = false;
        hud(T('detecting'));
        detect(p, (from) => {
            hud(from === 'manual' ? T('no_doc') : T('adjust_hint'), from === 'manual' ? 'warn' : null);
            p.prevWarp = null; p.out = null; p.thumb = null;
            drawFrame();
        });
    });
    $('dsFullFrame').addEventListener('click', () => {
        const p = S.pages[S.cur]; if (!p) return;
        p.quad = fullQuad(p.src.width, p.src.height);
        p.touched = true; p.from = 'manual';
        p.prevWarp = null; p.out = null; p.thumb = null;
        drawFrame();
    });

    // frame dragging
    const cv = $('dsCanvas');
    cv.addEventListener('pointerdown', (e) => framePointer(e, 'down'));
    cv.addEventListener('pointermove', (e) => framePointer(e, 'move'));
    window.addEventListener('pointerup', (e) => framePointer(e, 'up'));

    // doc pane
    $('dsFormats').addEventListener('click', (e) => {
        const b = e.target.closest('[data-x]'); if (!b) return;
        S.exp.fmt = b.dataset.x;
        lastBlob = null;
        syncDocControls();
    });
    $('dsSizes').addEventListener('click', (e) => {
        const b = e.target.closest('[data-s]'); if (!b) return;
        S.exp.size = b.dataset.s; setPref('pagesize', S.exp.size);
        lastBlob = null;
        syncDocControls();
    });
    $('dsQuality').addEventListener('click', (e) => {
        const b = e.target.closest('[data-q]'); if (!b) return;
        S.exp.quality = b.dataset.q; setPref('quality', S.exp.quality);
        lastBlob = null;
        syncDocControls();
    });
    $('dsFit').addEventListener('click', (e) => {
        const b = e.target.closest('[data-f]'); if (!b) return;
        S.exp.fitMb = +b.dataset.f || 0;
        setPref('fitmb', String(S.exp.fitMb));
        lastBlob = null;
        funnel('fit_set', { mb: S.exp.fitMb });
        syncDocControls();
    });
    $('dsDpi').addEventListener('click', (e) => {
        const b = e.target.closest('[data-d]'); if (!b) return;
        S.exp.dpi = b.dataset.d; setPref('dpi', S.exp.dpi);
        lastBlob = null;
        funnel('dpi_set', { dpi: S.exp.dpi });
        syncDocControls();
    });
    $('dsQNoteRetake').addEventListener('click', () => {
        const p = S.pages[S.cur];
        funnel('quality_retake', { v: p && p.quality && p.quality.verdict });
        // Drop this page and go straight back to where it came from. No undo
        // offer here: they were shown the page, told what was wrong with it and
        // chose to replace it — offering it back would be arguing with them.
        removePage(S.cur, true);
        if (S.cam.lastUsed) { openCamera(); } else { const i = fileInput(); if (i) i.click(); }
    });
    $('dsQNoteClose').addEventListener('click', () => {
        const p = S.pages[S.cur]; if (p) p.qNoteDismissed = true;
        hide($('dsQNote'));
        funnel('quality_kept', { v: p && p.quality && p.quality.verdict });
    });
    $('dsUndo').addEventListener('click', undoRemove);
    $('dsPrint').addEventListener('click', printPages);
    $('dsOcr').addEventListener('change', () => { S.exp.ocr = $('dsOcr').checked; lastBlob = null; });
    $('dsTwoUp').addEventListener('change', () => { S.exp.twoUp = $('dsTwoUp').checked; lastBlob = null; });
    $('dsName').addEventListener('input', () => { lastBlob = null; });
    $('dsShare').addEventListener('click', shareDoc);

    // dock
    $('dsCta').addEventListener('click', () => {
        if (S.view === 'page') commitPage();
        else if (S.view === 'doc') runExport();
    });
    $('dsTabs').addEventListener('click', (e) => {
        const b = e.target.closest('[data-tab]'); if (!b) return;
        const open = $('dsWork').classList.contains('is-sheet');
        if (S.view === 'page' && b.dataset.tab !== S.tab) { setTab(b.dataset.tab); setSheet(true); }
        else setSheet(!open);
        renderTabs();
    });
    $('dsGrip').addEventListener('click', () => { setSheet(false); renderTabs(); });
    // Tapping the document itself closes the sheet — the gesture everyone tries.
    $('dsStage').addEventListener('pointerdown', (e) => {
        if (!$('dsWork').classList.contains('is-sheet')) return;
        if ($('dsRail').contains(e.target)) return;
        setSheet(false); renderTabs();
    });
    $('dsGrip').addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSheet(false); renderTabs(); } });

    // keyboard
    window.addEventListener('keydown', (e) => {
        if (!S.open) return;
        if (e.key === 'Escape') {
            if ($('dsWork').classList.contains('is-sheet')) { setSheet(false); renderTabs(); }
            else $('dsBack').click();
        }
    });

    // re-lay the canvas when the box changes (rotation, sheet, window)
    let rT = 0;
    const relayout = () => {
        clearTimeout(rT);
        rT = setTimeout(() => {
            if (!S.open) return;
            if (S.view === 'page') { S.tab === 'frame' ? drawFrame() : paintOut(); }
            renderTabs();
        }, 90);
    };
    window.addEventListener('resize', relayout);
    window.addEventListener('orientationchange', relayout);

    // Work in progress must not vanish to a stray back-swipe.
    window.addEventListener('beforeunload', (e) => {
        stopCamera();
        if (S.pages.length) { e.preventDefault(); e.returnValue = ''; }
    });

    // A camera left running in a hidden tab is a lit LED and a flat battery.
    document.addEventListener('visibilitychange', () => {
        if (document.hidden && S.cam.stream) { stopCamera(); if (S.view === 'cam') go('doc'); }
    });

    stage('ready');
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
else init();
